import React, { useRef, useEffect, useState, useContext, useMemo, useLayoutEffect } from 'react';
import styles from './GanttChartAll.module.css';
import Procesory from './procesory_btn/Procesory';
import { AppContext } from 'context/AppContext';
import { druk_alert } from 'actions/druk_alert';
import { useGant } from './useGant';
import { RefreshCcw, X, XCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getNameOfElementTyp } from 'actions/getNameOfElementTyp';
import GanttContextMenu from './GanttContextMenu';

const GanttChartAll = ({ stages }) => {
  const chartRef = useRef(null);
  const scrollWrapperRef = useRef(null);

  const [minDate, setMinDate] = useState(null);
  const [etykieta, setEtykieta] = useState("");
  const [maxDate, setMaxDate] = useState(null);
  const [totalChartWidth, setTotalChartWidth] = useState(0);
  const [scaleFactor, setScaleFactor] = useState(0.5);
  
  const contextApp = useContext(AppContext);
  const procesory = contextApp.procesory;
  
  const [nowTime, setNowTime] = useState(new Date()); 
  const [colorFrom, setColorFrom] = useState(4);

  // Stan dla aktywnego zlecenia, daty spedycji oraz dynamicznych pozycji Y wierszy
  const [selectedZamowienieId, setSelectedZamowienieId] = useState(null);
  const [selectedShipmentDate, setSelectedShipmentDate] = useState(null);
  const [rowYPositions, setRowYPositions] = useState({});
      const [refreshGant,refreshGantAll,refreshGantZakonczone]= useGant();
  const [contextMenu, setContextMenu] = useState(null);

  const [showHourLines, setShowHourLines] = useState(true);

        const navigate = useNavigate();
      
  

  // Grupowanie etapów według procesora
  const groupedStages = useMemo(() => {
    return stages?.reduce((acc, stage) => {
      const { procesor_id } = stage;

      // wykluczone procesory harmonogramu
      if (procesor_id === 51 || procesor_id === 52) {
        return acc;
      }

      if (!acc[procesor_id]) {
        acc[procesor_id] = [];
      }
      acc[procesor_id].push(stage);
      return acc;
    }, {});
  }, [stages]);

  const processorIds = useMemo(() => Object.keys(groupedStages || {}), [groupedStages]);
  const allStagesList = useMemo(() => Object.values(groupedStages || {}).flat(), [groupedStages]);

useEffect(() => {
  if (allStagesList && allStagesList.length > 0) {
    const allDates = allStagesList.flatMap(stage => [new Date(stage.start), new Date(stage.end)]);

    if (allDates.length === 0) return;

    const calculatedMinDate = new Date(Math.min(...allDates));
    const calculatedMaxDate = new Date(Math.max(...allDates));

    // Cofamy o 1 godzinę I ZERUJEMY minuty, sekundy, ms
    calculatedMinDate.setHours(calculatedMinDate.getHours() - 1, 0, 0, 0);
    
    // Dodajemy 1 godzinę I USTAWIAMY minuty na pełną godzinę (lub 59:59)
    calculatedMaxDate.setHours(calculatedMaxDate.getHours() + 2, 0, 0, 0);

    setMinDate(calculatedMinDate);
    setMaxDate(calculatedMaxDate);

    const totalTimeSpanMinutes = (calculatedMaxDate.getTime() - calculatedMinDate.getTime()) / (1000 * 60);
    setTotalChartWidth(totalTimeSpanMinutes * scaleFactor);
  }
}, [allStagesList, scaleFactor]);

  // Dynamiczne wyliczenie dokładnego środka Y dla każdego wiersza w DOM
  useLayoutEffect(() => {
    if (!chartRef.current) return;
    
    const rows = chartRef.current.querySelectorAll(`.${styles.processorRow}`);
    const positions = {};

    processorIds.forEach((processorId, index) => {
      if (rows[index]) {
        positions[processorId] = rows[index].offsetTop + (rows[index].offsetHeight / 2);
      }
    });

    setRowYPositions(positions);
  }, [processorIds, totalChartWidth, scaleFactor, stages]);

  // Automatyczne przewijanie do pozycji "Dziś / TERAZ" po zmianie skali lub szerokości wykresu
  useEffect(() => {
    if (!minDate || !maxDate || totalChartWidth === 0) return;

    const timer = setTimeout(() => {
      handleScrollToNow();
    }, 50);

    return () => clearTimeout(timer);
  }, [scaleFactor, totalChartWidth]);

  // Obliczanie szerokości i przesunięcia klocka
  const calculateBarProperties = (start, end) => {
    if (!minDate || !maxDate || !totalChartWidth) {
      return { width: '0px', marginLeft: '0px', leftPx: 0, widthPx: 0 };
    }

    const totalTimeSpanMs = maxDate.getTime() - minDate.getTime();
    const stageDurationMs = new Date(end).getTime() - new Date(start).getTime();
    const startOffsetMs = new Date(start).getTime() - minDate.getTime();

    const widthPx = Math.max(0, (stageDurationMs / totalTimeSpanMs) * totalChartWidth);
    const marginLeftPx = Math.max(0, (startOffsetMs / totalTimeSpanMs) * totalChartWidth);

    return {
      width: `${widthPx}px`,
      marginLeft: `${marginLeftPx}px`,
      leftPx: marginLeftPx,
      widthPx: widthPx
    };
  };

  

  const getColor = (stage, technologiaId, zamowienieId) => {
    let parametr;

    if (colorFrom === 1) { 
      parametr = stage.klient_id;
      let hash = 0;
      const str = String(parametr);
      for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
      }
      const hue = Math.abs(hash) % 360; 
      return `hsl(${hue}, 75%, 60%)`;
    }

    if (colorFrom === 2) { 
      const hashString = `${technologiaId}-${zamowienieId}`;
      let hash = 0;
      for (let i = 0; i < hashString.length; i++) {
        hash = hashString.charCodeAt(i) + ((hash << 5) - hash);
      }
      const hue = (technologiaId * 137.508) % 360;
      const saturation = 70 + (hash % 30);
      const lightness = 50 + (hash % 10);
      return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
    }

    if (colorFrom === 3) { 
      parametr = stage.data_spedycji;
      let hash = 0;
      const str = String(parametr);
      for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
      }
      const hue = Math.abs(hash) % 360; 
      return `hsl(${hue}, 75%, 60%)`;
    }

    if (colorFrom === 4) { 
      if (stage.typ_grupy == 1) return `#dde2e7`;
      if (stage.status == 1) return `#dde2e7`;
      if (stage.status == 2) return `#a5cc52`;
      if (stage.status == 3) return `#c2b245`;
      if (stage.status == 4) return `#838383`;
    }
  };

  // Generowanie tła w soboty i niedziele
  const renderWeekendBackgrounds = () => {
    if (!minDate || !maxDate || totalChartWidth === 0) return null;

    const weekendBlocks = [];
    const totalTimeSpanMs = maxDate.getTime() - minDate.getTime();

    let currentDate = new Date(minDate);
    currentDate.setHours(0, 0, 0, 0);

    while (currentDate <= maxDate) {
      const dayOfWeek = currentDate.getDay();

      if (dayOfWeek === 6 || dayOfWeek === 0) {
        const dayStart = new Date(currentDate);
        const dayEnd = new Date(currentDate);
        dayEnd.setHours(23, 59, 59, 999);

        const actualStart = dayStart < minDate ? minDate : dayStart;
        const actualEnd = dayEnd > maxDate ? maxDate : dayEnd;

        const startOffsetMs = actualStart.getTime() - minDate.getTime();
        const durationMs = actualEnd.getTime() - actualStart.getTime();

        const leftPx = (startOffsetMs / totalTimeSpanMs) * totalChartWidth;
        const widthPx = (durationMs / totalTimeSpanMs) * totalChartWidth;

        weekendBlocks.push(
          <div
            key={`weekend-${currentDate.getTime()}`}
            className={styles.weekendOverlay}
            style={{
              left: `${leftPx}px`,
              width: `${widthPx}px`
            }}
          />
        );
      }

      currentDate.setDate(currentDate.getDate() + 1);
    }

    return weekendBlocks;
  };

const renderHourLines = () => {
    if (!showHourLines || !minDate || !maxDate || totalChartWidth === 0) return null; // Jeśli wyłączone, nic nie rysuj

    const lines = [];
    const interval = 24 * 60 * 60 * 1000;
    const startDate = new Date(minDate);
    startDate.setHours(0, 0, 0, 0);

    let currentDate = new Date(startDate);

    while (currentDate <= maxDate) {
      let hoursToAdd = scaleFactor < 0.3 ? [6, 18] : [0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22];

      hoursToAdd.forEach(hour => {
        const hourDate = new Date(currentDate);
        hourDate.setHours(hour, 0, 0, 0);

        if (hourDate >= minDate && hourDate <= maxDate) {
          const hourOffsetMs = hourDate.getTime() - minDate.getTime();
          const hourLeftPosition = (hourOffsetMs / (maxDate.getTime() - minDate.getTime())) * totalChartWidth;

          lines.push(
            <div 
              key={`hour-line-${hourDate.getTime()}`}
              className={styles.hourLineGrid}
              style={{ left: `${hourLeftPosition}px` }}
            />
          );
        }
      });

      currentDate.setTime(currentDate.getTime() + interval);
    }
    return lines;
  };

  const renderTimelineMarkers = () => {
    if (!minDate || !maxDate || totalChartWidth === 0) return null;

    const markers = [];
    const daysOfWeek = ['N', 'Pn', 'Wt', 'Śr', 'Czw', 'Pt', 'So'];
    const interval = 24 * 60 * 60 * 1000;
    
    const startDate = new Date(minDate);
    startDate.setHours(0, 0, 0, 0);

    let currentDate = new Date(startDate);

    while (currentDate <= maxDate) {
      const offsetMs = currentDate.getTime() - minDate.getTime();
      const leftPosition = (offsetMs / (maxDate.getTime() - minDate.getTime())) * totalChartWidth;

markers.push(
  <div 
    key={`day-${currentDate.getTime()}`} 
    className={styles.timelineMarker} 
    style={{ left: `${leftPosition}px`, transform: 'translateX(-50%)' }}
  >
    {daysOfWeek[currentDate.getDay()]} {String(currentDate.getDate()).padStart(2, '0')}/{String(currentDate.getMonth() + 1).padStart(2, '0')}
  </div>
);

      // let hoursToAdd = scaleFactor < 0.3 ? [6, 18] : [0, 3, 6, 9, 12, 15, 18, 21];
      let hoursToAdd = scaleFactor < 0.3 ? [6, 18] : [0, 2, 4, 6, 8, 10, 12, 14,16,18,20,22];

      hoursToAdd.forEach(hour => {
        const hourDate = new Date(currentDate);
        hourDate.setHours(hour, 0, 0, 0);

        if (hourDate >= minDate && hourDate <= maxDate) {
          const hourOffsetMs = hourDate.getTime() - minDate.getTime();
          const hourLeftPosition = (hourOffsetMs / (maxDate.getTime() - minDate.getTime())) * totalChartWidth;

markers.push(
  
    <div 
    key={`hour-${hourDate.getTime()}`} 
    className={styles.timelineSubMarker} 
    style={{ left: `${hourLeftPosition}px`, transform: 'translateX(-50%)' }}
  >
    {hour}:00
  </div>



);
        }
      });

      currentDate.setTime(currentDate.getTime() + interval);
    }


    
    return markers;
  };

  const calculateNowPosition = () => {
    if (!minDate || !maxDate || totalChartWidth === 0) return 0;
    const now = nowTime.getTime();
    const totalTimeSpanMs = maxDate.getTime() - minDate.getTime();
    const nowOffsetMs = now - minDate.getTime();
    const leftPosition = (nowOffsetMs / totalTimeSpanMs) * totalChartWidth;
    return Math.max(0, Math.min(leftPosition, totalChartWidth));
  };

  // Obliczanie pozycji dla wybranej daty spedycji
// Obliczanie pozycji dla wybranej daty spedycji (zawsze ustawiane na 6:00 rano)
const calculateShipmentPosition = () => {
  if (!minDate || !maxDate || totalChartWidth === 0 || !selectedShipmentDate) return 0;

  // 1. Tworzymy obiekt Date z wybranej daty spedycji
  const shipmentDateObj = new Date(selectedShipmentDate);

  // 2. Sztywno ustawiamy godzinę na 6:00:00
  shipmentDateObj.setHours(6, 0, 0, 0);

  const shipmentTime = shipmentDateObj.getTime();
  const totalTimeSpanMs = maxDate.getTime() - minDate.getTime();
  const shipmentOffsetMs = shipmentTime - minDate.getTime();
  const leftPosition = (shipmentOffsetMs / totalTimeSpanMs) * totalChartWidth;

  return Math.max(0, Math.min(leftPosition, totalChartWidth));
};

  const handleScaleChange = (event) => {
    setScaleFactor(parseFloat(event.target.value));
  };

  const handleScrollToNow = () => {
    if (!scrollWrapperRef.current || !minDate || !maxDate || totalChartWidth === 0) return;
    const now = new Date();
    const totalTimeSpanMs = maxDate.getTime() - minDate.getTime();
    const nowOffsetMs = now.getTime() - minDate.getTime();
    const scrollPosition = (nowOffsetMs / totalTimeSpanMs) * totalChartWidth;
    scrollWrapperRef.current.scrollLeft = scrollPosition - (scrollWrapperRef.current.clientWidth / 2);
  };

  // Wyliczanie linii łączących
  const connectionLines = useMemo(() => {
    if (!selectedZamowienieId || !minDate || !maxDate || !totalChartWidth) return [];

    const matchedStages = [];

    processorIds.forEach((processorId) => {
      const rowStages = groupedStages[processorId] || [];
      const y = rowYPositions[processorId] || 0;

      rowStages.forEach(stage => {
        if (stage.zamowienie_id === selectedZamowienieId) {
          const { leftPx, widthPx } = calculateBarProperties(stage.start, stage.end);

          matchedStages.push({
            startMs: new Date(stage.start).getTime(),
            outPoint: { x: leftPx + widthPx, y },
            inPoint: { x: leftPx, y }
          });
        }
      });
    });

    matchedStages.sort((a, b) => a.startMs - b.startMs);

    const lines = [];
    for (let i = 0; i < matchedStages.length - 1; i++) {
      lines.push({
        from: matchedStages[i].outPoint,
        to: matchedStages[i + 1].inPoint
      });
    }

    return lines;
  }, [selectedZamowienieId, groupedStages, processorIds, rowYPositions, minDate, maxDate, totalChartWidth]);

  return (
    <div className={styles.chartContainer}>
      <div className={styles.header}>
        <div className={styles.btnFormContainer}>
                                   <button title='Odśwież widok'className={styles.refreshBtn} onClick={() => { refreshGantAll() }}>
                          <RefreshCcw style={{ paddingBottom:'2px',color:'#25be11'}}  size={25}/>
            </button>

            {/* <button 
            className={`${styles.scrollToNowBtn} ${!showHourLines ? styles.inactiveBtn : ''}`}
            onClick={() => setShowHourLines(prev => !prev)}
            title="Włącz/wyłącz linie godzinowe"
          >
            {showHourLines ? 'Ukryj godziny' : 'Pokaż godziny'}
          </button>
             */}

    

        </div>

        <div className={styles.controls}>

               <button className={styles.scrollToNowBtn} onClick={handleScrollToNow}>
            Teraz
          </button>
          <span>Zoom:</span>
          <input
            type="range"
            min="0.05"
            max="1.0"
            step="0.05"
            value={scaleFactor}
            onChange={handleScaleChange}
          />
          <span style={{ marginLeft: '10px' }}>{scaleFactor.toFixed(2)}x</span>
     
                      <button 
              title="Zamknij"
              style={{background:'transparent', border:'none', marginRight:'10px'}}
              onClick={() => {navigate("/Panel");}}
            >
              <X style={{marginLeft:'20px', color:'#ffffffd4',height:'30px',width:'30px', padding:'5px',backgroundColor:'#ff0000', borderRadius:'5px'}} size={10} />
            </button>
        </div>
      </div>

      <div className={styles.ganttWrapper}>
        {/* LEWA KOLUMNA: NAZWY MASZYN */}
        <div className={styles.stageNamesList}>
          <div 
            className={styles.timeline} 
            style={{ visibility: 'hidden', pointerEvents: 'none' }}
          >
            <div className={styles.timelineMarkersContainer} />
            <div className={styles.timelineLine} />
          </div>
          
          {groupedStages && Object.keys(groupedStages).map(processorId => (
            <div 
              key={`names-group-${processorId}`} 
              className={styles.processorRow}
            >
              <div className={styles.processorNameFixed}>
                {/* {procesory.find(x => x.id == processorId)?.nazwa.substring(0, 15)} */}
                {procesory.find(x => x.id == processorId)?.nazwa}
              </div>
            </div>
          ))}
        </div>

        {/* PRAWA KOLUMNA: WYKRES GANTTA */}
        <div className={styles.scrollWrapper} ref={scrollWrapperRef}>
          <div
            ref={chartRef}
            className={styles.ganttChart}
            style={{ width: `${totalChartWidth}px`, position: 'relative' }}
          >
            {/* WARSTWA TŁA DLA SOBÓT I NIEDZIEL */}
            {renderWeekendBackgrounds()}

            
{renderHourLines()}
            {/* WARSTWA SVG DLA LINII ŁĄCZĄCYCH */}
            <svg
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 10
              }}
            >
              <defs>
                <marker
                  id="arrowhead"
                  markerWidth="6"
                  markerHeight="6"
                  refX="5"
                  refY="3"
                  orient="auto"
                >
                  <polygon points="0 0, 6 3, 0 6" fill="#ff4d4f" />
                </marker>
              </defs>

              {connectionLines.map((line, index) => {
                const { from, to } = line;
                const deltaX = Math.max(20, Math.abs(to.x - from.x) / 2);
                const pathData = `M ${from.x} ${from.y} C ${from.x + deltaX} ${from.y}, ${to.x - deltaX} ${to.y}, ${to.x} ${to.y}`;

                return (
                  <g key={`connection-${index}`}>
                    <path
                      d={pathData}
                      fill="none"
                      stroke="#4d9aff"
                      strokeWidth="2.5"
                      strokeDasharray="5 3"
                      markerEnd="url(#arrowhead)"
                    />
                    <circle cx={from.x} cy={from.y} r="3.5" fill="#ff4d4f" />
                  </g>
                );
              })}
            </svg>

            {/* GÓRNA OŚ CZASU */}
            <div className={styles.timeline}>
              <div className={styles.timelineMarkersContainer}>
                {renderTimelineMarkers()}
              </div>
              <div className={styles.timelineLine}></div>
            </div>

            <div 
              className={styles.nowLine} 
              style={{ left: `${calculateNowPosition()}px` }}
            >
              <span className={styles.nowLabel}>TERAZ</span>
            </div>  

            {/* LINIA DATA SPEDYCJI (POJAWIA SIĘ PO ZAZNACZENIU) */}
            {selectedShipmentDate && (
              <div 
                className={styles.nowLine} 
                style={{ 
                  left: `${calculateShipmentPosition()}px`, 
                  backgroundColor: '#efd808' 
                }}
              >
                <span className={styles.nowLabelSpedycja} style={{ backgroundColor: '#efd808' }}>SPEDYCJA</span>
              </div>
            )}

            {/* WIERSZE Z KLOCKAMI PRAC */}
            {groupedStages && Object.keys(groupedStages).map(processorId => (
              <div 
                key={`bars-group-${processorId}`} 
                className={styles.processorRow}
              >
                <div className={styles.processorBarsGroup}>
                  {groupedStages[processorId].map(stage => {
                    const { width, marginLeft } = calculateBarProperties(stage.start, stage.end);
                    const barColor = getColor(stage, stage.technologia_id, stage.zamowienie_id);
                    const isSelected = selectedZamowienieId === stage.zamowienie_id;

                    return (
                      <div
                        key={`bar-${stage.global_id}`}
                        className={styles.stageBar}
                        onContextMenu={(e) => {
  e.preventDefault(); // blokuje domyślne menu przeglądarki
  setContextMenu({
    x: e.clientX,
    y: e.clientY,
    stage: stage
  });
}}
                        style={{
                          width,
                          marginLeft,
                          backgroundColor: barColor,
                          outline: isSelected ? '2px solid #4d9aff' : 'none',
                          outlineOffset: '2px',
                          boxShadow: isSelected ? '0 0 8px rgba(255, 77, 79, 0.6)' : 'none',
                          cursor: 'pointer'
                        }}
                        onClick={() => {
                          const isSame = selectedZamowienieId === stage.zamowienie_id;
                          setSelectedZamowienieId(isSame ? null : stage.zamowienie_id);
                          setSelectedShipmentDate(isSame ? null : stage.data_spedycji);
                          setEtykieta(`Start: ${stage.start} - ${stage.end}      Ilość narządów: ${stage.ilosc_narzadow}      Przeloty: ${stage.przeloty} szt.`);
                        }}
                        title={` ${stage.name}\n od: ${stage.start}  do: ${stage.end}\n Ilość narządów: ${stage.ilosc_narzadow}\n Przeloty: ${stage.przeloty} szt.\n Spedycja: ${stage.data_spedycji}\n Status: ${stage.status}\n Element: ${getNameOfElementTyp( stage.typ_elementu,contextApp.typ_elementu)}`}
                      >
                        <span className={styles.nameText}>
                          {` ${stage.firma_nazwa}`}
                        </span>


                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* DOLNA OŚ CZASU */}
            <div className={styles.timeline}>
              <div className={styles.timelineMarkersContainer}>
                {renderTimelineMarkers()}
              </div>
              <div className={styles.timelineLine}></div>
            </div>
          </div>
        </div>
      </div>
      <GanttContextMenu
  contextMenu={contextMenu}
  onClose={() => setContextMenu(null)}
  onShowDetails={(stage) => {
    alert(`Szczegóły dla etapu: ${stage.name}`);
  }}
  onSelectOrder={(stage) => {
    setSelectedZamowienieId(stage.zamowienie_id);
    setSelectedShipmentDate(stage.data_spedycji);
  }}
/>
    </div>
  );
};

export default GanttChartAll;